import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography, Table, TableBody, TableCell, TableRow } from "@mui/material";
import {
	COLOR_PALETTE1,
	darkblueColor,
	lightBlueColor,
	lightBrownColor,
	lightYellowColor,
	midBlueColor,
	midGreenColor,
	offWhiteColor,
} from "components/Reports/data";
import DynamicVennDiagram from "./DynamicVennDiagram";
import { GlobalState } from "context/GlobalContext";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import { fastapiVmInstance } from "utils/AxiosInstances/RareboxFastApi";
/**
 * VennComposite.jsx
 * - Displays multiple Venn sections stacked vertically
 * - Each section = Title + D3 Venn Diagram + Summary Table
 * - Circles + intersection areas scale based on numeric values
 */
const sectionTypes = {
	online_offline: { name: "Online vs Offline ", key: "online_offline" },
	prepaid_postpaid: { name: "Prepaid Vs Postpaid ", key: "prepaid_postpaid" },
	eoss_full_price: { name: "Eoss Vs Full Price ", key: "eoss_full_price" },
};
const dummyResData = {
	online_offline: [
		{ sets: ["EBO"], customer_count: 30, contribution: "30%", growth_wise: "5%" },
		{ sets: ["D2C"], customer_count: 40, contribution: "40%", growth_wise: "10%" },
		{ sets: ["MP"], customer_count: 50, contribution: "50%", growth_wise: "-2%" },
		{ sets: ["EBO", "D2C"], customer_count: 10, contribution: "10%", growth_wise: "3%" },
		{ sets: ["EBO", "MP"], customer_count: 10, contribution: "10%", growth_wise: "1%" },
		{ sets: ["MP", "D2C"], customer_count: 15, contribution: "15%", growth_wise: "4%" },
		{ sets: ["EBO", "D2C", "MP"], customer_count: 5, contribution: "5%", growth_wise: "0%" },
	],
	prepaid_postpaid: [
		{ sets: ["PREPAID"], customer_count: 50, contribution: "50%", growth_wise: "8%" },
		{ sets: ["POSTPAID"], customer_count: 70, contribution: "70%", growth_wise: "12%" },
		{ sets: ["PREPAID", "POSTPAID"], customer_count: 20, contribution: "20%", growth_wise: "4%" },
	],
	eoss_full_price: [
		{ sets: ["EOSS"], customer_count: 60, contribution: "60%", growth_wise: "15%" },
		{ sets: ["FULL PRICE"], customer_count: 80, contribution: "80%", growth_wise: "20%" },
		{ sets: ["EOSS", "FULL PRICE"], customer_count: 25, contribution: "25%", growth_wise: "5%" },
	],
};

const tableColumns = {
	online_offline: {
		channels: {
			name: "CHANNELS",
			key: "channels",
			getValue: ({ row }) => {
				return row["sets"].join(" / ");
			},
		},
		customer_count: {
			name: "CUST",
			key: "customer_count",
			getValue: ({ row }) => {
				return row["customer_count"];
			},
		},
		contribution: {
			name: "CONT.",
			key: "contribution",
			getValue: ({ row }) => {
				return row["contribution"];
			},
		},
		growth_wise: {
			name: "GW",
			key: "growth_wise",
			getValue: ({ row }) => {
				return row["growth_wise"];
			},
		},
	},
	prepaid_postpaid: {
		mop: {
			name: "MOP",
			key: "mop",
			getValue: ({ row }) => {
				return row["sets"].join(" / ");
			},
		},
		customer_count: {
			name: "CUST",
			key: "customer_count",
			getValue: ({ row }) => {
				return row["customer_count"];
			},
		},
		contribution: {
			name: "CONT.",
			key: "contribution",
			getValue: ({ row }) => {
				return row["contribution"];
			},
		},
		growth_wise: {
			name: "GW",
			key: "growth_wise",
			getValue: ({ row }) => {
				return row["growth_wise"];
			},
		},
	},
	eoss_full_price: {
		discount_type: {
			name: "Discount Type",
			key: "discount_type",
			getValue: ({ row }) => {
				return row["sets"].join(" / ");
			},
		},
		customer_count: {
			name: "CUST",
			key: "customer_count",
			getValue: ({ row }) => {
				return row["customer_count"];
			},
		},
		contribution: {
			name: "CONT.",
			key: "contribution",
			getValue: ({ row }) => {
				return row["contribution"];
			},
		},
		growth_wise: {
			name: "GW",
			key: "growth_wise",
			getValue: ({ row }) => {
				return row["growth_wise"];
			},
		},
	},
};

const TableSection = ({ section, data, colors }) => {
	return (
		<Table
			size="small"
			sx={{
				borderCollapse: "collapse",
				width: "100%",
				// border: (theme) => `1px solid ${theme.palette.divider}`,
			}}>
			<TableBody>
				{/* Header Row */}
				<TableRow>
					{Object.keys(tableColumns[section.key]).map((colKey, idx) => (
						<TableCell
							key={idx}
							sx={{
								borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
								borderRight: (theme) => idx === 0 ? `1px solid ${theme.palette.divider}` : "none",
								verticalAlign: "middle",
								textAlign: "center",
								// py: 0.5,
								px: 1,
							}}>
							<Typography fontSize={13} fontWeight={600}>
								{tableColumns[section.key][colKey].name}
							</Typography>
						</TableCell>
					))}
				</TableRow>

				{/* Data Rows */}
				{data?.map((row, i) => (
					<TableRow key={i}>
						{Object.keys(tableColumns[section.key]).map((colKey, idx) => {
							const getValueFun = tableColumns[section.key][colKey].getValue;
							const value = getValueFun({ row });

							// For color matching
							const colorKey = row.sets.length > 1 ? row.sets.join("∩") : row.sets[0];
							const color = colors?.[colorKey] || "#ccc";

							return (
								<TableCell
									key={idx}
									sx={{
										borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
										borderRight: (theme) => idx === 0 ? `1px solid ${theme.palette.divider}` : "none",
										verticalAlign: "middle",
										textAlign: "center",
										py: 0.5,
										px: 1,
									}}>
									{/* Add color box only in first column */}
									{idx === 0 ? (
										<Stack direction="row" justifyContent={"center"} alignItems="center" spacing={1}>
											<Box
												sx={{
													width: 12,
													height: 12,
													borderRadius: "3px",
													bgcolor: color,
													flexShrink: 0,
													border: "1px solid #ddd",
												}}
											/>
											<Typography fontSize={13}>{value}</Typography>
										</Stack>
									) : (
										<Typography
											fontSize={13}
											color={
												colKey === "growth_wise" && row[colKey]?.startsWith("-")
													? "error.main"
													: colKey === "growth_wise"
													? "success.main"
													: "text.primary"
											}>
											{value}
										</Typography>
									)}
								</TableCell>
							);
						})}
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
};

const CustomerVennDiagrams = ({ filter, ...rest }) => {
	const { setSnackBarOpen, setSnackBar, snackbar } = GlobalState();

	const [resData, setResData] = useState(dummyResData);
	const [loading, setLoading] = useState(false);

	const colors = {
		online_offline: {
			D2C: COLOR_PALETTE1.slateGray,
			MP: COLOR_PALETTE1.beige,
			EBO: COLOR_PALETTE1.midBlueColor,
			"EBO∩D2C": lightYellowColor,
			"EBO∩MP": offWhiteColor,
			"MP∩D2C": COLOR_PALETTE1.dustyBlue,
			"EBO∩D2C∩MP": midGreenColor,
		},
		prepaid_postpaid: {
			PREPAID: COLOR_PALETTE1.slateGray,
			POSTPAID: COLOR_PALETTE1.beige,
			"PREPAID∩POSTPAID": COLOR_PALETTE1.dustyBlue,
		},
		eoss_full_price: {
			EOSS: COLOR_PALETTE1.slateGray,
			"FULL PRICE": COLOR_PALETTE1.beige,
			"EOSS∩FULL PRICE": COLOR_PALETTE1.dustyBlue,
		},
	};

	const fetch = async () => {
		try {
			setLoading(true);
			const body = { filter };

			const res = await fastapiVmInstance.post(`/api/summary-report/sales-aop`, {filter});

			if (res.data.status === "error") throw new Error(res.data?.message);
			const result = res.data.result;
			setResData(result);
			setLoading(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				message: parseErrMsgFromError(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	useEffect(() => {
		// fetch();
	}, [filter]);
	return (
		<Stack spacing={1} sx={{ width: "100%", background: "white" }} {...rest}>
			<Stack
				spacing={1}
				sx={{
					borderBottom: "1px solid #eee",
				}}>
				<Typography variant="h6" sx={{ p: 1 }} width="100%">
					{sectionTypes.online_offline.name} (Draft)
				</Typography>
				<Box display="flex" alignItems="center" justifyContent="space-around" flexWrap="wrap">
					<Box width="40%">
						<DynamicVennDiagram
							data={resData[sectionTypes.online_offline.key]}
							colors={colors[sectionTypes.online_offline.key]}
							size={{ width: 250, height: 220 }}
							hidelabel
							formatDataFun={(row) => {
								return { sets: row.sets, size: row.customer_count };
							}}
						/>
					</Box>

					{/* TABLE */}
					<Box width="50%" sx={{ zoom: 0.7, transformOrigin: "center" }}>
						<TableSection
							section={sectionTypes.online_offline}
							data={resData[sectionTypes.online_offline.key]}
							colors={colors[sectionTypes.online_offline.key]}
						/>
					</Box>
				</Box>
			</Stack>
			<Stack
				sx={{
					borderBottom: "1px solid #eee",
				}}>
				<Typography variant="h6" sx={{ p: 1 }} width="100%">
					{sectionTypes.prepaid_postpaid.name} (Draft)
				</Typography>
				<Box display="flex" alignItems="center" justifyContent="space-around" flexWrap="wrap">
					<Box width="40%">
						<DynamicVennDiagram
							data={resData[sectionTypes.prepaid_postpaid.key]}
							colors={colors[sectionTypes.prepaid_postpaid.key]}
							hidelabel
							size={{ width: 220, height: 180 }}
							formatDataFun={(row) => {
								return { sets: row.sets, size: row.customer_count };
							}}
						/>
					</Box>

					{/* TABLE */}
					<Box width="50%" sx={{ zoom: 0.8, transformOrigin: "center" }}>
						<TableSection
							section={sectionTypes.prepaid_postpaid}
							data={resData[sectionTypes.prepaid_postpaid.key]}
							colors={colors[sectionTypes.prepaid_postpaid.key]}
						/>
					</Box>
				</Box>
			</Stack>
			<Stack
				sx={{
					borderBottom: "1px solid #eee",
				}}>
				<Typography variant="h6" sx={{ p: 1 }} width="100%">
					{sectionTypes.eoss_full_price.name} (Draft)
				</Typography>
				<Box display="flex" alignItems="center" justifyContent="space-around" flexWrap="wrap">
					<Box width="40%">
						<DynamicVennDiagram
							data={resData[sectionTypes.eoss_full_price.key]}
							colors={colors[sectionTypes.eoss_full_price.key]}
							hidelabel
							size={{ width: 220, height: 170 }}
							formatDataFun={(row) => {
								return { sets: row.sets, size: row.customer_count };
							}}
						/>
					</Box>

					{/* TABLE */}
					<Box width="50%" sx={{ zoom: 0.8, transformOrigin: "center" }}>
						<TableSection
							section={sectionTypes.eoss_full_price}
							data={resData[sectionTypes.eoss_full_price.key]}
							colors={colors[sectionTypes.eoss_full_price.key]}
						/>
					</Box>
				</Box>
			</Stack>
		</Stack>
	);
};

export default CustomerVennDiagrams;
